/**
 * Background tasks feature — Ducks slice.
 * Canonical source of LLM-backed Temporal workflow state for the UI:
 * header tray + enhance-button enable/disable survive refresh because this
 * slice is rehydrated from GET /api/v1/tasks on boot and mutated live via
 * task.started / task.updated WS broadcasts.
 */

import { BackgroundTask } from '@/infra/types/domain';

// ─── LLM task-type allowlist ──────────────────────────────
//
// The tray surfaces only workflows whose output a human is expected to
// review (LLM-backed). Add new task_types here as they ship.
export const LLM_TASK_TYPES: ReadonlySet<string> = new Set(['enhance_contact']);

function isLlmTask(t: BackgroundTask | undefined | null): boolean {
  return !!t && LLM_TASK_TYPES.has(t.task_type);
}

// ─── State ────────────────────────────────────────────────

export interface BackgroundTasksState {
  items: Record<string, BackgroundTask>;
  order: string[]; // newest first, by started_at desc
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  oldestStartedAt: string | null;
  hasMore: boolean;
  popoverOpen: boolean;
}

const initialState: BackgroundTasksState = {
  items: {},
  order: [],
  loading: false,
  loadingMore: false,
  error: null,
  oldestStartedAt: null,
  hasMore: false,
  popoverOpen: false,
};

// ─── Action Types ─────────────────────────────────────────

export const FETCH_TASKS_REQUESTED = 'backgroundTasks/fetchRequested';
export const FETCH_TASKS_SUCCEEDED = 'backgroundTasks/fetchSucceeded';
export const FETCH_TASKS_FAILED = 'backgroundTasks/fetchFailed';
export const LOAD_MORE_REQUESTED = 'backgroundTasks/loadMoreRequested';
export const LOAD_MORE_SUCCEEDED = 'backgroundTasks/loadMoreSucceeded';
export const LOAD_MORE_FAILED = 'backgroundTasks/loadMoreFailed';
export const TASK_UPSERTED = 'backgroundTasks/upserted';
export const TASK_REMOVED = 'backgroundTasks/removed';
export const TOGGLE_POPOVER = 'backgroundTasks/togglePopover';
export const CLOSE_POPOVER = 'backgroundTasks/closePopover';
export const TASK_CLICKED = 'backgroundTasks/clicked';

// Client-only id prefix for optimistic entries created before the WS round-trip
// completes. A real task.started WS broadcast supersedes the optimistic row via
// the TASK_UPSERTED reducer's matching-(task_type, subject_id) dedup.
export const OPTIMISTIC_ID_PREFIX = 'optimistic:';
export const optimisticTaskId = (taskType: string, subjectId: string) =>
  `${OPTIMISTIC_ID_PREFIX}${taskType}:${subjectId}`;

// ─── Action Creators ──────────────────────────────────────

export const fetchTasksRequested = () => ({ type: FETCH_TASKS_REQUESTED } as const);
export const fetchTasksSucceeded = (items: BackgroundTask[], hasMore: boolean) =>
  ({ type: FETCH_TASKS_SUCCEEDED, payload: { items, hasMore } } as const);
export const fetchTasksFailed = (error: string) =>
  ({ type: FETCH_TASKS_FAILED, payload: error } as const);
export const loadMoreRequested = () => ({ type: LOAD_MORE_REQUESTED } as const);
export const loadMoreSucceeded = (items: BackgroundTask[], hasMore: boolean) =>
  ({ type: LOAD_MORE_SUCCEEDED, payload: { items, hasMore } } as const);
export const loadMoreFailed = (error: string) =>
  ({ type: LOAD_MORE_FAILED, payload: error } as const);
export const taskUpserted = (task: BackgroundTask) =>
  ({ type: TASK_UPSERTED, payload: task } as const);
export const taskRemoved = (id: string) =>
  ({ type: TASK_REMOVED, payload: id } as const);
export const togglePopover = () => ({ type: TOGGLE_POPOVER } as const);
export const closePopover = () => ({ type: CLOSE_POPOVER } as const);
export const taskClicked = (task: BackgroundTask) =>
  ({ type: TASK_CLICKED, payload: task } as const);

type Action =
  | ReturnType<typeof fetchTasksRequested>
  | ReturnType<typeof fetchTasksSucceeded>
  | ReturnType<typeof fetchTasksFailed>
  | ReturnType<typeof loadMoreRequested>
  | ReturnType<typeof loadMoreSucceeded>
  | ReturnType<typeof loadMoreFailed>
  | ReturnType<typeof taskUpserted>
  | ReturnType<typeof taskRemoved>
  | ReturnType<typeof togglePopover>
  | ReturnType<typeof closePopover>
  | ReturnType<typeof taskClicked>;

// ─── Helpers ──────────────────────────────────────────────

function mergeTasks(state: BackgroundTasksState, tasks: BackgroundTask[]): BackgroundTasksState {
  const items = { ...state.items };
  const existing = new Set(state.order);
  const appended: string[] = [];
  for (const t of tasks) {
    if (!isLlmTask(t)) continue;
    items[t.id] = t;
    if (!existing.has(t.id)) {
      appended.push(t.id);
      existing.add(t.id);
    }
  }
  const order = [...state.order, ...appended];
  // Keep order sorted newest first by started_at.
  order.sort((a, b) => {
    const ta = items[a]?.started_at ?? '';
    const tb = items[b]?.started_at ?? '';
    return tb.localeCompare(ta);
  });
  const oldest = order.length ? items[order[order.length - 1]]?.started_at ?? null : null;
  return { ...state, items, order, oldestStartedAt: oldest };
}

// ─── Reducer ──────────────────────────────────────────────

export function backgroundTasksReducer(
  state: BackgroundTasksState = initialState,
  action: Action
): BackgroundTasksState {
  switch (action.type) {
    case FETCH_TASKS_REQUESTED:
      return { ...state, loading: true, error: null };
    case FETCH_TASKS_SUCCEEDED: {
      const { items: list, hasMore } = action.payload;
      // Full-refresh path: replace items+order with fetched list, preserving
      // any already-upserted-via-WS items that aren't in the response.
      const items: Record<string, BackgroundTask> = { ...state.items };
      const order: string[] = [];
      for (const t of list) {
        if (!isLlmTask(t)) continue;
        items[t.id] = t;
        order.push(t.id);
      }
      // Merge any pre-existing items not returned (e.g. rows that arrived via
      // WS between request and response) by appending them and re-sorting.
      for (const id of state.order) {
        if (!order.includes(id)) order.push(id);
      }
      order.sort((a, b) => {
        const ta = items[a]?.started_at ?? '';
        const tb = items[b]?.started_at ?? '';
        return tb.localeCompare(ta);
      });
      const oldest = order.length ? items[order[order.length - 1]]?.started_at ?? null : null;
      return {
        ...state,
        loading: false,
        error: null,
        items,
        order,
        oldestStartedAt: oldest,
        hasMore,
      };
    }
    case FETCH_TASKS_FAILED:
      return { ...state, loading: false, error: action.payload };
    case LOAD_MORE_REQUESTED:
      return { ...state, loadingMore: true, error: null };
    case LOAD_MORE_SUCCEEDED: {
      const merged = mergeTasks(state, action.payload.items);
      return { ...merged, loadingMore: false, hasMore: action.payload.hasMore };
    }
    case LOAD_MORE_FAILED:
      return { ...state, loadingMore: false, error: action.payload };
    case TASK_UPSERTED: {
      const t = action.payload;
      if (!isLlmTask(t)) return state;
      const items = { ...state.items, [t.id]: t };
      let order = state.order;
      // If a real (non-optimistic) task arrives matching an optimistic row's
      // (task_type, subject_id), drop the optimistic entry so we don't render
      // both. This is the handoff from the optimistic placeholder to the
      // authoritative row produced by RecordTaskStart.
      if (!t.id.startsWith(OPTIMISTIC_ID_PREFIX) && t.subject_id) {
        const optimisticKey = optimisticTaskId(t.task_type, String(t.subject_id));
        if (items[optimisticKey] && optimisticKey !== t.id) {
          delete items[optimisticKey];
          order = order.filter((id) => id !== optimisticKey);
        }
      }
      if (!state.items[t.id]) {
        order = [t.id, ...order];
      }
      // Resort so a newly-started row floats to the top and updated rows stay
      // in their started_at position.
      order = [...order].sort((a, b) => {
        const ta = items[a]?.started_at ?? '';
        const tb = items[b]?.started_at ?? '';
        return tb.localeCompare(ta);
      });
      const oldest = order.length ? items[order[order.length - 1]]?.started_at ?? null : null;
      return { ...state, items, order, oldestStartedAt: oldest };
    }
    case TASK_REMOVED: {
      const id = action.payload;
      if (!state.items[id]) return state;
      const items = { ...state.items };
      delete items[id];
      const order = state.order.filter((x) => x !== id);
      const oldest = order.length ? items[order[order.length - 1]]?.started_at ?? null : null;
      return { ...state, items, order, oldestStartedAt: oldest };
    }
    case TOGGLE_POPOVER:
      return { ...state, popoverOpen: !state.popoverOpen };
    case CLOSE_POPOVER:
      return { ...state, popoverOpen: false };
    default:
      return state;
  }
}

// ─── Selectors ────────────────────────────────────────────

type StateWithBg = { backgroundTasks: BackgroundTasksState };

export const selectBackgroundTasksState = (s: StateWithBg): BackgroundTasksState =>
  s.backgroundTasks ?? initialState;

export const selectOrderedBackgroundTasks = (s: StateWithBg): BackgroundTask[] => {
  const st = selectBackgroundTasksState(s);
  return st.order.map((id) => st.items[id]).filter(Boolean);
};

export const selectRunningTaskCount = (s: StateWithBg): number => {
  const st = selectBackgroundTasksState(s);
  let n = 0;
  for (const id of st.order) {
    if (st.items[id]?.status === 'running') n += 1;
  }
  return n;
};

export const selectPopoverOpen = (s: StateWithBg): boolean =>
  selectBackgroundTasksState(s).popoverOpen;

export const selectHasMore = (s: StateWithBg): boolean =>
  selectBackgroundTasksState(s).hasMore;

export const selectLoading = (s: StateWithBg): boolean =>
  selectBackgroundTasksState(s).loading;

export const selectLoadingMore = (s: StateWithBg): boolean =>
  selectBackgroundTasksState(s).loadingMore;

/**
 * Returns the most recent running task matching (taskType, subjectId), or null.
 * Used by ContactCard to disable Enhance while a workflow is in-flight.
 */
export const selectRunningTaskForSubject = (
  s: StateWithBg,
  args: { taskType: string; subjectId: string }
): BackgroundTask | null => {
  const st = selectBackgroundTasksState(s);
  for (const id of st.order) {
    const t = st.items[id];
    if (!t) continue;
    if (t.status !== 'running') continue;
    if (t.task_type !== args.taskType) continue;
    if (t.subject_id !== args.subjectId) continue;
    return t;
  }
  return null;
};

/**
 * Most recent task (any status) for (taskType, subjectId). Used for label
 * selection — e.g. "Retry Enhance" after a failed run.
 */
export const selectLatestTaskForSubject = (
  s: StateWithBg,
  args: { taskType: string; subjectId: string }
): BackgroundTask | null => {
  const st = selectBackgroundTasksState(s);
  for (const id of st.order) {
    const t = st.items[id];
    if (!t) continue;
    if (t.task_type !== args.taskType) continue;
    if (t.subject_id !== args.subjectId) continue;
    return t;
  }
  return null;
};
